'use client';

import {
  type ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { useReducedMotion } from '../../hooks/use-reduced-motion';
import { useToast } from './hooks/use-toast';
import {
  TOAST_PROVIDER_ITEM_TRANSITION_DURATION_MS,
  toastProviderItemRecipe,
} from './styles/toast-provider-item.styles';
import { toastProviderListRecipe } from './styles/toast-provider-list.styles';
import { ToastProviderItem } from './toast-provider-item';
import type {
  ToastProviderListItemInterface,
  ToastProviderListPropsInterface,
  ToastProviderListToastElementPropsInterface,
} from './types/toast-provider-list.types';

/**
 * ToastProvider.List ingests toasts from context and renders them in a list
 */
export const ToastProviderList = forwardRef<HTMLUListElement, ToastProviderListPropsInterface>(
  (props: ToastProviderListPropsInterface, forwardedRef) => {
    const {
      className,
      css: cssProp,
      renderToast = (toast) => <ToastProviderItem key={toast.id} toast={toast} />,
      ...rest
    } = props;
    const { placement, toasts } = useToast();

    const reducedMotion = useReducedMotion();
    const [visibleToasts, setVisibleToasts] = useState<Array<ToastProviderListItemInterface>>([]);
    const exitTimeoutIdsRef = useRef(new Map<number, number>());

    useIsomorphicLayoutEffect(() => {
      setVisibleToasts((currentVisibleToasts) => {
        if (reducedMotion) {
          return toasts.map((toast) => ({ toast, animationState: 'open' }));
        }

        const activeToastIds = new Set(toasts.map((toast) => toast.id));
        const toastsById = new Map(toasts.map((toast) => [toast.id, toast]));
        const nextVisibleToasts: Array<ToastProviderListItemInterface> = [];

        currentVisibleToasts.forEach((item) => {
          if (activeToastIds.has(item.toast.id)) {
            const toast = toastsById.get(item.toast.id);

            if (toast) {
              nextVisibleToasts.push({
                toast,
                animationState:
                  item.animationState === 'exiting' ? 'entering' : item.animationState,
              });
            }
          } else {
            nextVisibleToasts.push({ ...item, animationState: 'exiting' });
          }
        });

        const nextVisibleToastIds = new Set(nextVisibleToasts.map((item) => item.toast.id));

        toasts.forEach((toast) => {
          if (!nextVisibleToastIds.has(toast.id)) {
            nextVisibleToasts.push({ toast, animationState: 'entering' });
          }
        });

        return nextVisibleToasts;
      });
    }, [reducedMotion, toasts]);

    useEffect(() => {
      if (reducedMotion || !visibleToasts.some((item) => item.animationState === 'entering')) {
        return;
      }

      let firstAnimationFrameId = 0;
      let secondAnimationFrameId = 0;

      firstAnimationFrameId = window.requestAnimationFrame(() => {
        secondAnimationFrameId = window.requestAnimationFrame(() => {
          setVisibleToasts((currentVisibleToasts) =>
            currentVisibleToasts.map((item) =>
              item.animationState === 'entering' ? { ...item, animationState: 'open' } : item,
            ),
          );
        });
      });

      return () => {
        window.cancelAnimationFrame(firstAnimationFrameId);
        window.cancelAnimationFrame(secondAnimationFrameId);
      };
    }, [reducedMotion, visibleToasts]);

    useEffect(() => {
      if (reducedMotion) {
        exitTimeoutIdsRef.current.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
        exitTimeoutIdsRef.current.clear();
        return;
      }

      const visibleToastIds = new Set(visibleToasts.map((item) => item.toast.id));

      exitTimeoutIdsRef.current.forEach((timeoutId, toastId) => {
        if (!visibleToastIds.has(toastId)) {
          window.clearTimeout(timeoutId);
          exitTimeoutIdsRef.current.delete(toastId);
        }
      });

      visibleToasts.forEach((item) => {
        if (item.animationState !== 'exiting' || exitTimeoutIdsRef.current.has(item.toast.id)) {
          return;
        }

        const timeoutId = window.setTimeout(() => {
          exitTimeoutIdsRef.current.delete(item.toast.id);
          setVisibleToasts((currentVisibleToasts) =>
            currentVisibleToasts.filter((currentItem) => currentItem.toast.id !== item.toast.id),
          );
        }, TOAST_PROVIDER_ITEM_TRANSITION_DURATION_MS);

        exitTimeoutIdsRef.current.set(item.toast.id, timeoutId);
      });
    }, [reducedMotion, visibleToasts]);

    useEffect(() => {
      const exitTimeoutIds = exitTimeoutIdsRef.current;

      return () => {
        exitTimeoutIds.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
        exitTimeoutIds.clear();
      };
    }, []);

    const listStyles = css(toastProviderListRecipe.raw({ placement }), cssProp);

    return (
      <ul
        ref={forwardedRef}
        className={cx('c11n', 'c11n_toast-provider__list', listStyles, className)}
        data-c11n-component="ToastProvider.List"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
      >
        {visibleToasts.map(({ toast, animationState }) => {
          const toastElement = renderToast(toast);

          if (!isValidElement(toastElement)) {
            return toastElement;
          }

          const itemAnimationStyles = css(toastProviderItemRecipe.raw({ animationState }));
          const itemClassName = (toastElement.props as ToastProviderListToastElementPropsInterface)
            .className;

          return cloneElement(
            toastElement as ReactElement<ToastProviderListToastElementPropsInterface>,
            {
              className: cx('c11n', itemAnimationStyles, itemClassName),
            },
          );
        })}
      </ul>
    );
  },
);

ToastProviderList.displayName = 'ToastProvider.List';
